import { useCallback, useState } from 'react';

const useDownloadFile = (
    baseUrl,
    urlPattern = ':id',
    mimeType = '*/*',
    includeCredentials = false
) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const buildUrl = useCallback(
        (identifier) => {
            if (!identifier) return baseUrl;
            const url = urlPattern.replace(':id', identifier);
            return `${baseUrl}/${url}`.replace(/([^:]\/)\/+/g, '$1');
        },
        [baseUrl, urlPattern]
    );

    const downloadFile = useCallback(
        async (identifier, filename) => {
            setIsLoading(true);
            setError(null);

            try {
                const url = buildUrl(identifier);
                const response = await fetch(url, {
                    method: 'GET',
                    credentials: includeCredentials ? 'include' : 'omit',
                    headers: {
                        Accept: mimeType,
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentType = response.headers.get('content-type');
                if (!contentType || !contentType.includes(mimeType === '*/*' ? '' : mimeType)) {
                    throw new Error(`Received content is not of type ${mimeType}`);
                }

                const blob = await response.blob();
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            } catch (error) {
                setError(error.message);
                console.error('Download error:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [buildUrl, mimeType, includeCredentials]
    );

    return { downloadFile, isLoading, error };
};

export default useDownloadFile;
